import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { orderId, type OrderId } from './Order.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.order_line_item_ */
export type OrderLineItemId = string & { __brand: 'OrderLineItemId' }

/** Represents the table public.order_line_item_ */
export default interface OrderLineItemTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<OrderLineItemId, OrderLineItemId | undefined, OrderLineItemId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  erpnextQuotationItemId: ColumnType<
    string | null,
    string | null,
    string | null
  >

  /** Database type: pg_catalog.text */
  erpnextSalesOrderItemId: ColumnType<
    string | null,
    string | null,
    string | null
  >

  /** Database type: pg_catalog.int4 */
  quantity: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  lineItemName: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  lineItemDescription: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.numeric */
  lineItemRateDollars: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  lineItemAmountDollars: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  lineItemUnitWeightLbs: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  lineItemTotalWeightLbs: ColumnType<
    number | null,
    number | null,
    number | null
  >

  /** Database type: pg_catalog.uuid */
  orderId: ColumnType<OrderId, OrderId, OrderId>

  /** Database type: pg_catalog.text */
  erpnextBomId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  itemCode: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  erpnextDeliveryNoteItemId: ColumnType<
    string | null,
    string | null,
    string | null
  >

  /** Database type: pg_catalog.text */
  erpnextSalesInvoiceItemId: ColumnType<
    string | null,
    string | null,
    string | null
  >

  /** Database type: pg_catalog.bool */
  serviceItemsCreated: ColumnType<boolean, boolean | undefined, boolean>

  /** Database type: pg_catalog.float8 */
  loggingIntervalHours: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  transmissionIntervalHours: ColumnType<
    number | null,
    number | null,
    number | null
  >

  /** Database type: pg_catalog.int4 */
  dataNumberOfDevices: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.int4 */
  dataDuration: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.int4 */
  rentalDurationMonths: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  rentalMonthlyRate: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.float8 */
  rentalTotalRate: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.timestamptz */
  rentalStartDate: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.text */
  itemUnits: ColumnType<string | null, string | null, string | null>
}

export type OrderLineItem = Selectable<OrderLineItemTable>

export type OrderLineItemInitializer = Insertable<OrderLineItemTable>

export type OrderLineItemMutator = Updateable<OrderLineItemTable>

export const orderLineItemId =
  z.string() as unknown as z.Schema<OrderLineItemId>

export const orderLineItem = z.object({
  id: orderLineItemId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  erpnextQuotationItemId: z.string().nullable().optional(),
  erpnextSalesOrderItemId: z.string().nullable().optional(),
  quantity: z.number().nullable().optional(),
  lineItemName: z.string().nullable().optional(),
  lineItemDescription: z.string().nullable().optional(),
  lineItemRateDollars: z.number().nullable().optional(),
  lineItemAmountDollars: z.number().nullable().optional(),
  lineItemUnitWeightLbs: z.number().nullable().optional(),
  lineItemTotalWeightLbs: z.number().nullable().optional(),
  orderId: orderId,
  erpnextBomId: z.string().nullable().optional(),
  itemCode: z.string().nullable().optional(),
  erpnextDeliveryNoteItemId: z.string().nullable().optional(),
  erpnextSalesInvoiceItemId: z.string().nullable().optional(),
  serviceItemsCreated: z.boolean(),
  loggingIntervalHours: z.number().nullable().optional(),
  transmissionIntervalHours: z.number().nullable().optional(),
  dataNumberOfDevices: z.number().nullable().optional(),
  dataDuration: z.number().nullable().optional(),
  rentalDurationMonths: z.number().nullable().optional(),
  rentalMonthlyRate: z.number().nullable().optional(),
  rentalTotalRate: z.number().nullable().optional(),
  rentalStartDate: z.coerce.date().nullable().optional(),
  itemUnits: z.string().nullable().optional(),
}) as unknown as z.Schema<OrderLineItem>

export const orderLineItemInitializer = z.object({
  id: orderLineItemId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  erpnextQuotationItemId: z.string().optional().nullable(),
  erpnextSalesOrderItemId: z.string().optional().nullable(),
  quantity: z.number().optional().nullable(),
  lineItemName: z.string().optional().nullable(),
  lineItemDescription: z.string().optional().nullable(),
  lineItemRateDollars: z.number().optional().nullable(),
  lineItemAmountDollars: z.number().optional().nullable(),
  lineItemUnitWeightLbs: z.number().optional().nullable(),
  lineItemTotalWeightLbs: z.number().optional().nullable(),
  orderId: orderId,
  erpnextBomId: z.string().optional().nullable(),
  itemCode: z.string().optional().nullable(),
  erpnextDeliveryNoteItemId: z.string().optional().nullable(),
  erpnextSalesInvoiceItemId: z.string().optional().nullable(),
  serviceItemsCreated: z.boolean().optional(),
  loggingIntervalHours: z.number().optional().nullable(),
  transmissionIntervalHours: z.number().optional().nullable(),
  dataNumberOfDevices: z.number().optional().nullable(),
  dataDuration: z.number().optional().nullable(),
  rentalDurationMonths: z.number().optional().nullable(),
  rentalMonthlyRate: z.number().optional().nullable(),
  rentalTotalRate: z.number().optional().nullable(),
  rentalStartDate: z.coerce.date().optional().nullable(),
  itemUnits: z.string().optional().nullable(),
}) as unknown as z.Schema<OrderLineItemInitializer>

export const orderLineItemMutator = z.object({
  id: orderLineItemId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  erpnextQuotationItemId: z.string().optional().nullable(),
  erpnextSalesOrderItemId: z.string().optional().nullable(),
  quantity: z.number().optional().nullable(),
  lineItemName: z.string().optional().nullable(),
  lineItemDescription: z.string().optional().nullable(),
  lineItemRateDollars: z.number().optional().nullable(),
  lineItemAmountDollars: z.number().optional().nullable(),
  lineItemUnitWeightLbs: z.number().optional().nullable(),
  lineItemTotalWeightLbs: z.number().optional().nullable(),
  orderId: orderId.optional(),
  erpnextBomId: z.string().optional().nullable(),
  itemCode: z.string().optional().nullable(),
  erpnextDeliveryNoteItemId: z.string().optional().nullable(),
  erpnextSalesInvoiceItemId: z.string().optional().nullable(),
  serviceItemsCreated: z.boolean().optional(),
  loggingIntervalHours: z.number().optional().nullable(),
  transmissionIntervalHours: z.number().optional().nullable(),
  dataNumberOfDevices: z.number().optional().nullable(),
  dataDuration: z.number().optional().nullable(),
  rentalDurationMonths: z.number().optional().nullable(),
  rentalMonthlyRate: z.number().optional().nullable(),
  rentalTotalRate: z.number().optional().nullable(),
  rentalStartDate: z.coerce.date().optional().nullable(),
  itemUnits: z.string().optional().nullable(),
}) as unknown as z.Schema<OrderLineItemMutator>
